//built-in Libraries

//Third party libraries
import { EnvelopeIcon, LockClosedIcon, LockOpenIcon, UserCircleIcon } from '@heroicons/react/24/solid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
//Services
import { getUser, postReInviteUser, type UpdateUserPayload, updateUser, userQueryKeys } from '@/api/user';
import { HasPermission } from '@/components/Permission/HasPermission';
import AccentTitle from '@/components/UI/AccentTitle';
import Badge from '@/components/UI/Badge';
import Button from '@/components/UI/Button';
import { SingleEditableField } from '@/components/UI/Form/EditableField';
//Assets and Miscellaneous
import LoadingUserDetail from '@/components/UI/loading/LoadingUserDetail';
//Custom components
import Confirm from '@/components/UI/Modal/Confirm';
import { useOrgTitles } from '@/hooks/ReactQueryHooks/useTitlesQuery';
import { notifyUser } from '@/store/Toast';
import { sanitizeResponseError } from '@/utils';
import { invalidateUserDetailAndLists } from '../userQueryCache';

//Component Begin
export default function UserDetail() {
  const { userId } = useParams<string>();
  const numericUserId = Number(userId);
  const dispatch = useDispatch();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isProfilePictureUrlError, setIsProfilePictureUrlError] = useState(false);

  const [userDetailError, setUserDetailError] = useState('');
  const queryClient = useQueryClient();

  const fetchOrgTitles = useOrgTitles({});
  const titles = fetchOrgTitles?.data?.data;

  const reinviteUser = useMutation((data: { userId: number }) => postReInviteUser(data.userId), {
    onSuccess: () => {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Resend user invite successful',
          toastType: 'success',
        })
      );
    },
    onError: () => {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Resend user invite failed',
          toastType: 'failed',
        })
      );
    },
  });

  //Tanstack: version 5 query implementation
  const fetchUser = useQuery({
    queryKey: userQueryKeys.detail(numericUserId),
    queryFn: () => getUser(numericUserId),
    enabled: Number.isInteger(numericUserId) && numericUserId > 0,
    onError: (error: unknown) => {
      const resError = sanitizeResponseError(error);
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: resError,
          toastType: 'failed',
        })
      );
      setUserDetailError(resError);
    },
  });
  const user = fetchUser?.data?.data;

  const handleUserSuspend = () => {
    if (!user) return;

    patchUser.mutate({ active: false });
    setShowDeleteConfirm(false);
  };

  const patchUser = useMutation((data: UpdateUserPayload) => updateUser(numericUserId, data), {
    onSuccess: async () => {
      await invalidateUserDetailAndLists(queryClient, numericUserId);
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'User update successful',
          toastType: 'success',
        })
      );
    },
    onError: () => {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'User update failed',
          toastType: 'failed',
        })
      );
    },
  });

  const updateUserHandler = (data: { title: string }) => {
    if (!user) return;

    const titleId = Number(data.title);
    if (user.titleId !== titleId) {
      patchUser.mutate({ titleId });
    } else {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'No changes were detected to save the deliverable.',
          toastType: 'info',
        })
      );
    }
  };

  const titlesOptions = titles?.map((title) => ({
    label: title.name,
    value: title.id.toString(),
  }));

  return (
    <div className='w-full'>
      {showDeleteConfirm && user && (
        <Confirm
          title='Suspend User'
          message={`Are you sure you want to suspend ${user.name} ? This action cannot be reversed.`}
          acceptButtonLabel='Suspend'
          declineButtonLabel='Cancel'
          accept={handleUserSuspend}
          decline={() => setShowDeleteConfirm(false)}
        />
      )}
      <AccentTitle title='User Profile' />
      {fetchUser.isLoading && <LoadingUserDetail />}
      {!fetchUser.isLoading && user && (
        <main className='w-full mx-auto p-6 border-t'>
          <div className='overflow-hidden sm:rounded-lg'>
            <div className='pb-10 flex justify-between'>
              <div className='flex items-center space-x-5 px-6'>
                {user.profileImageUrl && !isProfilePictureUrlError && (
                  <div className='shrink-0'>
                    <div className='relative'>
                      <img
                        className='h-16 w-16 rounded-full'
                        onError={() => setIsProfilePictureUrlError(true)}
                        src={user.profileImageUrl}
                        alt='profile'
                      />
                      <span className='absolute inset-0 shadow-inner rounded-full' aria-hidden='true' />
                    </div>
                  </div>
                )}
                {(!user.profileImageUrl || isProfilePictureUrlError) && (
                  <span className='inline-flex items-center justify-center h-20 w-20 rounded-full bg-neutralColor-lighter'>
                    <span className='font-medium leading-none text-green-500 font-bold text-xl'>
                      {user.name.split(' ')[0].charAt(0) + user.name.split(' ')[user.name.split(' ').length - 1].charAt(0)}
                    </span>
                  </span>
                )}
                <div>
                  <h2 className='m-0 text-neutralColor-medium truncate w-96'>{user.name}</h2>
                  <span className='text-sm leading-5 text-neutralColor-normal'>{user.email}</span>
                </div>
              </div>
              <div className='bg-white px-4 py-5 flex sm:gap-4 sm:px-6'>
                <HasPermission of='UPDATE_USER'>
                  {!user.active && !user.firstInvite && (
                    <Button
                      label='Activate User'
                      icon={<LockOpenIcon className='h-4 w-4' />}
                      btnStyle='btn-neutral'
                      onClick={() =>
                        patchUser.mutate({
                          active: true,
                        })
                      }
                    />
                  )}
                </HasPermission>
                <HasPermission of='DELETE_USER'>
                  {user.active && !user.firstInvite && (
                    <Button
                      label='Suspend User'
                      btnStyle='btn-neutral'
                      icon={<LockClosedIcon className='h-4 w-4' />}
                      onClick={() => setShowDeleteConfirm(true)}
                    />
                  )}
                </HasPermission>
                <HasPermission of='UPDATE_USER'>
                  {!user.active && user.firstInvite && (
                    <Button
                      label='Resend Invite'
                      icon={<EnvelopeIcon className='h-4 w-4' />}
                      btnStyle='btn-neutral'
                      onClick={() =>
                        reinviteUser.mutate({
                          userId: user.id,
                        })
                      }
                    />
                  )}
                </HasPermission>
              </div>
            </div>
            <dl className='pb-5 space-y-8 px-5'>
              <div className='sm:grid sm:grid-cols-6'>
                <dt className='text-sm font-medium text-gray-500'>Full name</dt>
                <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-5'>{user.name}</dd>
              </div>
              <div className='sm:grid sm:grid-cols-6'>
                <dt className='text-sm font-medium text-gray-500'>Title</dt>
                <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-5'>
                  <SingleEditableField
                    name='title'
                    type='select'
                    inputValue={user.title}
                    permission='UPDATE_USER'
                    onSubmit={updateUserHandler}
                    isLoading={patchUser.isLoading}
                    isSuccess={patchUser.isSuccess}
                    options={titlesOptions}
                  />
                </dd>
              </div>
              <div className='sm:grid sm:grid-cols-6'>
                <dt className='text-sm font-medium text-gray-500'>Role</dt>
                <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-5'>{user.role}</dd>
              </div>
              <div className='sm:grid sm:grid-cols-6'>
                <dt className='text-sm font-medium text-gray-500'>Email address</dt>
                <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-5'>{user.email}</dd>
              </div>
              {/* <div className="px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Office Address</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    123 Main Street, New York, NY 10024
                  </dd>
                </div> */}

              <HasPermission of='UPDATE_USER'>
                <div className='sm:grid sm:grid-cols-6'>
                  <dt className='text-sm font-medium text-gray-500'>Invited Date</dt>
                  <dd className='mt-1 text-sm text-gray-900 sm:mt-0'>{new Date(user.createdAt).toLocaleString()}</dd>
                </div>
              </HasPermission>

              <HasPermission of='UPDATE_USER'>
                <div className='sm:grid sm:grid-cols-6'>
                  <dt className='text-sm font-medium text-gray-500'>Invitation Status</dt>
                  <dd className='flex gap-4 mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-5'>
                    {user.active && !user.firstInvite && <p>Joined</p>}
                    {!user.active && user.firstInvite && <p>Sent Invitation</p>}
                  </dd>
                </div>
              </HasPermission>

              <HasPermission of='UPDATE_USER'>
                <div className='bg-white sm:grid sm:grid-cols-6'>
                  <dt className='text-sm font-medium text-gray-500'>Account</dt>
                  <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-5'>
                    {!user.active && !user.firstInvite && <Badge badgeType='tertiary' text='Suspended' />}
                    {user.active && !user.firstInvite && <Badge badgeType='primary' text='Active' />}
                    {!user.active && user.firstInvite && <Badge badgeType='secondary' text='Inactive' />}
                  </dd>
                </div>
              </HasPermission>
            </dl>
          </div>
        </main>
      )}
      {!fetchUser.isLoading && !user && userDetailError.includes('User not found') && (
        <div className='flex justify-center items-center w-full h-screen/2'>
          <div className='flex flex-col justify-center items-center text-center rounded p-6 max-w-xl'>
            <UserCircleIcon className='h-10 w-10 text-primaryColor-normal' />
            <h2>User Not Found</h2>
            <p>Uh oh!!! The user your are looking for does not exist in the system. Please validate the user account and try again.</p>
          </div>
        </div>
      )}
    </div>
  );
}
